import { Dispatch, SetStateAction, useState } from 'react'
import {
  Avatar,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material'

import { useMe } from 'src/hooks'
import { UserSessionFormModal } from '../user-session-form'

import { IUserSessionFormValues, IUserSessionsDto } from 'src/types'
import { getTableTimestamp } from 'src/common/utils'
import { useStyles } from './styles'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { EyeOpenedIcon } from 'src/assets/icons/v2/EyeOpenedIcons'

interface IErrorLogsTableProps {
  usersSessions?: IUserSessionsDto[] | void
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

const UserSessionsTable = ({ usersSessions, sort, setSort }: IErrorLogsTableProps) => {
  const [formValues, setFormValues] = useState<IUserSessionFormValues>(null)
  const { data: user } = useMe()
  const classes = useStyles()

  const theme = useTheme()

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const handleOpenFormModal = (values: IUserSessionFormValues) => () => {
    setFormValues(values)
  }

  const handleCloseFormModal = () => {
    setFormValues(null)
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow 
              style={{ border: 0 }} 
              sx={{ 'td, th': { border: 0 } }}
            >
              <TableCell 
                align="left" 
                width="35%"
              >
                <TableSortLabel
                  onClick={handleSortChange('user_email')}
                  active={sort.split('-')[0] === 'user_email'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-user-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell 
                align="center" 
                width="35%"
              >
                <TableSortLabel
                  onClick={handleSortChange('last_login_at')}
                  active={sort.split('-')[0] === 'last_login_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="users-sessions-last-login-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell 
                align="center" 
                width="20%"
              >
                <TableSortLabel
                  onClick={handleSortChange('active')}
                  active={sort.split('-')[0] === 'active'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px",
                    ml: '8px'
                  }}
                >
                  <HelperWrapper t="account-users-status-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell 
                align="center" 
                width="10%" 
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {usersSessions?.map((row) => (
              <TableRow
                key={row.id}
                style={{
                  borderTop: `1px solid ${theme.palette.custom.grey}`,
                  borderBottom: `1px solid ${theme.palette.custom.grey}`,
                }}
                sx={{ 'td, th': { border: 0 } }}
              >
                <TableCell 
                  align="center" 
                  width="35%"
                >
                  <div className={classes.iconWrapper}>
                    {row.user && (
                      <Avatar
                        src={row.user.image_small || row.user.image}
                        alt={row.user.email}
                        variant="circular"
                      />
                    )}
                    <div className={classes.textWrapper}>
                      <div>{row.user?.email ?? row.user_email}</div>
                      {row.user && (
                        <>
                          <br />
                          {row.user.name}
                        </>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell 
                  align="center" 
                  width="35%"
                >
                  {getTableTimestamp({ date: row.last_login_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell 
                  align="center" 
                  width="20%"
                >
                  <Chip
                    className={row.active ? classes.activeStatusChip : classes.inactiveStatusChip}
                    label={row.active ? 'online' : 'offline'}
                  />
                </TableCell>
                <TableCell 
                  align="center" 
                  width="10%"
                >
                  <div onClick={handleOpenFormModal(row)} className={classes.button}>
                    <EyeOpenedIcon size={24} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <UserSessionFormModal
        handleClose={handleCloseFormModal}
        formValues={formValues}
        open={!!formValues}
      />
    </>
  )
}

export { UserSessionsTable }
